import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useBillingLifecycle } from "@/lib/billingLifecycle";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

export function useTrialView(ref, event, enabled) {
  const recorded = useRef(false);
  useEffect(() => {
    if (!enabled || recorded.current || !ref.current) return;
    const observer = new IntersectionObserver(entries => {
      if (!entries.some(entry => entry.isIntersecting) || recorded.current) return;
      recorded.current = true;
      api.post(`/billing/trial-view/${event}`).catch(() => { recorded.current = false; });
      observer.disconnect();
    });
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref, event, enabled]);
}

export function TrialPlansMarker() {
  const { data } = useBillingLifecycle();
  const ref = useRef(null);
  useTrialView(ref, "billing_plans_viewed", data?.plan === "trial" && data?.can_manage_billing);
  return <div ref={ref} id="fieldlink-plans" className="h-px scroll-mt-24" />;
}

export default function TrialProgress({ compact = false }) {
  const { data, trialValue, valueError, refreshValue } = useBillingLifecycle();
  const ref = useRef(null);
  const enabled = data?.plan === "trial" && data?.can_manage_billing;
  useTrialView(ref, "trial_value_viewed", enabled && !!trialValue);
  useEffect(() => {
    if (enabled && window.location.hash === "#trial-progress") ref.current?.scrollIntoView({ block: "start" });
  }, [enabled]);
  if (!enabled) return null;
  const milestones = trialValue?.milestones || [];
  const known = milestones.filter(m => m.completed !== null);
  const completed = known.filter(m => m.completed).length;
  const fit = data.tiers?.find(t => t.id === data.suggested_plan);
  return <section ref={ref} id="trial-progress" className="my-6 scroll-mt-24" aria-label="Trial progress" data-testid="trial-progress">
    <div className="hard-card p-4 md:p-5">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="font-display font-bold uppercase tracking-wide text-lg">{compact ? "Your existing Fieldlink workspace" : "Your Fieldlink trial"}</h2>
        {!compact && data.trial_days_remaining != null && <p className="text-sm font-semibold">{data.trial_days_remaining} {data.trial_days_remaining === 1 ? "day" : "days"} remaining</p>}
      </div>
      {!trialValue ? <p className="mt-3 text-sm text-zinc-500">{valueError ? <>Trial activity is unavailable. <button className="underline" onClick={refreshValue}>Retry</button></> : "Loading trial activity…"}</p> : <>
        <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-400">{trialValue.has_meaningful_activity ? "Real records already captured in your Fieldlink workspace." : "Start with the parts of Fieldlink that fit your business."}</p>
        {trialValue.period_available ? <p className="mt-1 text-xs text-zinc-500">Trial records: {new Date(trialValue.period_start).toLocaleDateString("en-NZ")}–{new Date(trialValue.period_end).toLocaleDateString("en-NZ")}. Active jobs show current state.</p> : <p className="mt-1 text-xs text-zinc-500">Trial dates are unavailable; only current-state counts are shown.</p>}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-4">
          {trialValue.metrics.map(metric => <div key={metric.key} className="border border-zinc-200 dark:border-zinc-700 p-3 rounded-sm">
            <p className="text-2xl font-bold tabular-nums">{metric.value.toLocaleString("en-NZ")}</p>
            <p className="text-xs text-zinc-600 dark:text-zinc-400">{metric.label}</p>
            <p className="text-[10px] text-zinc-500 mt-1">{metric.scope === "trial" ? "During trial" : "Currently planned / in progress"}</p>
          </div>)}
        </div>
        <p className="mt-3 text-xs text-zinc-500">Record counts, not revenue or savings. Quotes and invoices include drafts, exclude void/cancelled records. Work timesheets include pending and approved submissions. Updated about every five minutes.</p>
      </>}
      {!compact && <div className="mt-4 border-t border-zinc-200 dark:border-zinc-700 pt-4 text-sm">
        <p className="font-semibold">Keep Fieldlink running</p>
        <p className="mt-1 text-zinc-600 dark:text-zinc-400">{data.seat_usage} active users. {fit?.max_staff != null ? `${fit.name} supports up to ${fit.max_staff} users and fits your current team size.` : "Contact Fieldlink about custom team capacity."} Your company data is safe.</p>
        <Button asChild className="mt-3 rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white"><a href="#fieldlink-plans">View plans</a></Button>
      </div>}
    </div>
    {!compact && !data.locked && trialValue && <div className="mt-4 border border-zinc-200 dark:border-zinc-700 p-4 rounded-sm">
      <div className="flex flex-wrap justify-between gap-2"><h3 className="text-sm font-bold uppercase">Get the most from your trial</h3><span className="text-xs text-zinc-500">{completed} of {known.length} available steps completed</span></div>
      <p className="text-xs text-zinc-500 mt-1">Optional next steps. Use only the modules your company needs. Team membership reflects current active accounts; other steps use trial records.</p>
      <ul className="grid md:grid-cols-2 gap-x-6 gap-y-3 mt-4 text-sm">{milestones.map(m => <li key={m.key} className="flex items-center justify-between gap-2">
        <span><span aria-hidden="true" className={m.completed ? "text-emerald-700 mr-2" : "text-zinc-400 mr-2"}>{m.completed ? "✓" : "○"}</span>{m.label}{m.completed === null && <span className="text-xs text-zinc-500"> · Dates unavailable</span>}</span>
        {m.completed === false && <Link className="text-xs font-semibold text-[#C9470A] dark:text-orange-300 underline" to={m.route}>Open →</Link>}
      </li>)}</ul>
    </div>}
  </section>;
}
